import { FC } from 'react';
import KpiCard from '@/components/admin/KpiCard';
import type { KpisVisaoGeral as Kpis } from '@/utils/equiplexVisaoGeral';
import { fmtCompacto, fmtData, fmtPct, fmtUn } from '@/utils/painelFormat';
import LinhaRegua from '../LinhaRegua';

interface Props {
  kpis: Kpis;
  corte: string;
  foraDoPlano: { skus: number; unidades: number };
}

function subRitmo(k: Kpis, corte: string): string {
  if (k.esperadoAteCorte === null) return 'sem plano S&OP no mês';
  if (k.ritmoAte) return `dias fechados, até ${fmtData(k.ritmoAte).slice(0, 5)}: esperado ${fmtCompacto(k.esperadoAteCorte)}`;
  const ate = k.emAndamento ? `até ${fmtData(corte).slice(0, 5)}` : 'no mês';
  return `esperado ${ate}: ${fmtCompacto(k.esperadoAteCorte)}`;
}

function subMedia(k: Kpis): string {
  if (k.necessariaDiaria !== null) return `para fechar o plano: ${fmtCompacto(k.necessariaDiaria)}/dia`;
  if (k.ritmoAte) return `${k.diasFechados} dias fechados de ${k.dias}`;
  return `${k.diasDecorridos} de ${k.dias} dias com dado`;
}

/**
 * Os números do mês escolhido, no recorte de formatos do gráfico. O "ritmo" compara com
 * o plano proporcional aos dias que já passaram — é ele que diz se o mês fecha.
 */
const KpisVisaoGeral: FC<Props> = ({ kpis, corte, foraDoPlano }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-4" data-testid="vg-kpis">
    <KpiCard
      icon="flag"
      value={kpis.plano === null ? '—' : fmtCompacto(kpis.plano)}
      label="Plano S&OP do mês"
      sublabel={kpis.plano === null ? 'mês sem demanda no Balanceamento' : 'demanda do Balanceamento de Linha'}
    />
    <KpiCard
      icon="precision_manufacturing"
      value={fmtCompacto(kpis.realizado)}
      label="Produzido no mês"
      sublabel={kpis.pctDoPlano === null ? 'sem plano para comparar' : `${fmtPct(kpis.pctDoPlano)} do plano do mês`}
    >
      <LinhaRegua>
        Reportado nas OPs que começaram no mês, sem reprocesso: o CP0511 do Datasul sem as OPs 100…, o mesmo número do S&OP
        e do Relatório Daily. Cresce até as OPs do mês serem reportadas.
      </LinhaRegua>
    </KpiCard>
    <KpiCard icon="speed" value={fmtPct(kpis.aderenciaRitmo)} label="Aderência ao ritmo" sublabel={subRitmo(kpis, corte)} />
    <KpiCard
      icon="calendar_today"
      value={kpis.mediaDiaria === null ? '—' : fmtCompacto(kpis.mediaDiaria)}
      label="Média por dia"
      sublabel={subMedia(kpis)}
    />
    <KpiCard
      icon="event_available"
      value={kpis.ultimoDia ? fmtCompacto(kpis.ultimoDia.un) : '—'}
      label="Último dia com produção"
      sublabel={kpis.ultimoDia ? fmtData(kpis.ultimoDia.data) : 'nenhuma produção reportada no mês'}
    />
    <KpiCard
      icon="call_split"
      value={fmtUn(foraDoPlano.skus)}
      label="SKUs fora do plano"
      sublabel={foraDoPlano.skus ? `${fmtCompacto(foraDoPlano.unidades)} unidades produzidas` : 'tudo o que saiu estava no plano'}
    />
  </div>
);

export default KpisVisaoGeral;
